/**
 * Browser half of a passkey SIGN-IN on the account host (issue 698, ADR 0093).
 *
 * A phone-only account has no address and no password, so without this the one
 * host that exists for its legal obligations (D27) — takeout, closure, erasure —
 * would have no door for it at all.
 *
 * A local copy of the authentication half of workspace-ui's `utils/passkey.ts`,
 * on purpose (ADR 0001: no shared runtime code across apps). Only signing in
 * lives here — passkeys are created on a workspace host, never on this one — and
 * the base64url ⇄ ArrayBuffer conversion is the part that must stay byte for byte
 * what `@simplewebauthn/server` produces and expects.
 */

export function passkeysSupported(): boolean {
  return typeof window !== 'undefined' && !!window.PublicKeyCredential && !!navigator.credentials;
}

export function b64urlToBuffer(value: string): ArrayBuffer {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

export function bufferToB64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** The shape `@simplewebauthn/server` verifies. */
export interface AssertionPayload {
  id: string;
  rawId: string;
  type: string;
  clientExtensionResults: Record<string, unknown>;
  response: Record<string, string | undefined>;
}

function serializeAssertion(credential: PublicKeyCredential): AssertionPayload {
  const response = credential.response as AuthenticatorAssertionResponse;
  const payload: AssertionPayload = {
    id: credential.id,
    rawId: bufferToB64url(credential.rawId),
    type: credential.type,
    clientExtensionResults: credential.getClientExtensionResults() as Record<string, unknown>,
    response: { clientDataJSON: bufferToB64url(response.clientDataJSON) },
  };
  if (response.authenticatorData) payload.response.authenticatorData = bufferToB64url(response.authenticatorData);
  if (response.signature) payload.response.signature = bufferToB64url(response.signature);
  if (response.userHandle) payload.response.userHandle = bufferToB64url(response.userHandle);
  return payload;
}

/**
 * The modal authentication ceremony: the browser offers the passkeys it holds
 * for the relying party, and the assertion says whose they are. No conditional
 * (autofill) variant — this host's form is a button, not an address field.
 */
export async function getPasskeyAssertion(options: any): Promise<AssertionPayload> {
  const credential = (await navigator.credentials.get({
    publicKey: {
      ...options,
      challenge: b64urlToBuffer(options.challenge),
      allowCredentials: (options.allowCredentials || []).map((c: any) => ({ ...c, id: b64urlToBuffer(c.id) })),
    },
  })) as PublicKeyCredential | null;
  if (!credential) throw new Error('passkey sign-in was cancelled');
  return serializeAssertion(credential);
}

/**
 * Whether a rejected ceremony is the person's own doing. `NotAllowedError` is a
 * dismissed or timed-out prompt (or no passkey for this site), `AbortError` one
 * we called off: neither is a failure, and neither puts an error on screen.
 * Anything else is a bug or a misconfiguration and is left to propagate.
 */
export function ceremonyDismissed(error: unknown): boolean {
  const name = typeof error === 'object' && error !== null ? String((error as { name?: unknown }).name || '') : '';
  return name === 'NotAllowedError' || name === 'AbortError';
}
